import { useState, MouseEvent } from 'react';
import type { Label } from '@/types/general';
import { Controller, useFormContext } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/VisibilityRounded';
import VisibilityOff from '@mui/icons-material/VisibilityOffRounded';
import useTranslations from '@/hooks/useTranslations';

interface PasswordInputProps {
  field: string;
  label: Label;
  placeholder?: Label;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
}

export default function PasswordInput({
  field,
  label,
  placeholder = '',
  required = false,
  disabled,
  error,
  errorMessage,
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const { t, getLocale } = useTranslations();
  const { control } = useFormContext();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Controller
      name={field as never}
      control={control}
      render={({ field: controllerField }) => (
        <TextField
          {...controllerField}
          value={controllerField.value || ''}
          label={getLocale(label)}
          type={showPassword ? 'text' : 'password'}
          required={required}
          placeholder={getLocale(placeholder) || t('form.enterValue')}
          margin='none'
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={t('formFields.togglePasswordVisibility')}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge='end'
                    size='small'
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          disabled={disabled}
          error={error}
          helperText={errorMessage}
          sx={{
            // label text: default, not red
            '& label.Mui-error': {
              color: 'text.secondary',
            },
            // asterisk: default, not red
            '& .MuiFormLabel-asterisk.Mui-error': {
              color: 'text.secondary',
            },
            // border: default, not red
            '& .MuiInputBase-root.Mui-error': {
              borderColor: 'error.main',
            },
          }}
        />
      )}
    />
  );
}
